import React, { useState, useRef, useEffect } from "react";
import { useNotifications } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import {
    Bell,
    GraduationCap,
    ScrollText,
    FileText,
    Mail,
    Package,
    TrendingUp,
    Info,
    Check,
    CheckCheck,
    Trash2,
    X,
    Laptop,
    Briefcase,
    HardHat,
} from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { AppNotification, NotificationType } from "../types";

const typeIconMap: Record<NotificationType, React.ReactNode> = {
    training_plan: <GraduationCap size={16} className="text-blue-500" />,
    policy: <ScrollText size={16} className="text-purple-500" />,
    memo: <FileText size={16} className="text-amber-500" />,
    procedure: <FileText size={16} className="text-green-500" />,
    correspondence: <Mail size={16} className="text-rose-500" />,
    inventory: <Package size={16} className="text-orange-500" />,
    development: <TrendingUp size={16} className="text-teal-500" />,
    business_system: <Laptop size={16} className="text-indigo-500" />,
    recruitment: <Briefcase size={16} className="text-pink-500" />,
    safety: <HardHat size={16} className="text-cyan-600" />,
    stock: <Package size={16} className="text-amber-600" />,
    general: <Info size={16} className="text-gray-500" />,
};

const typeColorMap: Record<NotificationType, string> = {
    training_plan: "bg-blue-50 border-blue-200",
    policy: "bg-purple-50 border-purple-200",
    memo: "bg-amber-50 border-amber-200",
    procedure: "bg-green-50 border-green-200",
    correspondence: "bg-rose-50 border-rose-200",
    inventory: "bg-orange-50 border-orange-200",
    development: "bg-teal-50 border-teal-200",
    business_system: "bg-indigo-50 border-indigo-200",
    recruitment: "bg-pink-50 border-pink-200",
    safety: "bg-cyan-50 border-cyan-200",
    stock: "bg-amber-50 border-amber-200",
    general: "bg-gray-50 border-gray-200",
};

function formatTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export const NotificationBell: React.FC<{ variant?: "floating" | "inline" }> = ({ variant = "floating" }) => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } =
        useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleNotificationClick = async (notification: AppNotification) => {
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
            setIsOpen(false);
        }
    };

    return (
        <div
            className={cn(
                variant === "floating"
                    ? "fixed bottom-6 right-6 z-[100] md:bottom-8 md:right-8"
                    // Below `sm` the wrapper stays static so the panel anchors to the
                    // fixed top bar (full viewport width) instead of the bell. Anchored
                    // to the bell, a 340px panel overflows the left edge on phones.
                    : "sm:relative"
            )}
            ref={panelRef}
        >
            {/* Dropdown Panel — opens upward when floating, downward when inline */}
            {isOpen && (
                <div
                    className={cn(
                        "absolute w-[340px] sm:w-[380px] max-h-[420px] sm:max-h-[480px] bg-white dark:bg-gray-800",
                        "rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700",
                        "overflow-hidden",
                        "animate-in fade-in duration-200",
                        variant === "floating"
                            ? "right-0 bottom-14 slide-in-from-bottom-2"
                            // right-3 mirrors the top bar's own px-3 gutter below `sm`;
                            // from `sm` up the panel hangs off the bell again.
                            : "right-3 sm:right-0 top-full mt-2 max-w-[calc(100vw-1.5rem)] slide-in-from-top-2"
                    )}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-sm">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Notifications
                            </h3>
                            {unreadCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                                    {unreadCount}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                    onClick={markAllAsRead}
                                >
                                    <CheckCheck size={14} className="mr-1" />
                                    Mark all read
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-gray-400 hover:text-gray-600"
                                onClick={() => setIsOpen(false)}
                            >
                                <X size={14} />
                            </Button>
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="overflow-y-auto max-h-[340px] sm:max-h-[400px] custom-scrollbar">
                        {notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                                    <Bell size={20} className="text-gray-400" />
                                </div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    No notifications yet
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    You're all caught up!
                                </p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={cn(
                                        "group relative flex gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50",
                                        "transition-colors duration-150 cursor-pointer",
                                        notification.isRead
                                            ? "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750"
                                            : "bg-blue-50/50 dark:bg-blue-900/10 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                    )}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    {/* Unread indicator */}
                                    {!notification.isRead && (
                                        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    )}

                                    {/* Icon */}
                                    <div
                                        className={cn(
                                            "flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border",
                                            typeColorMap[notification.type] || typeColorMap.general
                                        )}
                                    >
                                        {typeIconMap[notification.type] || typeIconMap.general}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <p
                                            className={cn(
                                                "text-sm leading-snug",
                                                notification.isRead
                                                    ? "text-gray-600 dark:text-gray-400"
                                                    : "text-gray-900 dark:text-gray-100 font-medium"
                                            )}
                                        >
                                            {notification.message}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                {formatTimeAgo(notification.createdAt)}
                                            </span>
                                            {notification.triggeredBy && (
                                                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                    • by {notification.triggeredBy.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions (visible on hover) */}
                                    <div className="flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {!notification.isRead && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-gray-400 hover:text-blue-500 hover:bg-blue-50"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsRead(notification.id);
                                                }}
                                                title="Mark as read"
                                            >
                                                <Check size={14} />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-gray-400 hover:text-red-500 hover:bg-red-50"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(notification.id);
                                            }}
                                            title="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Bell Button */}
            <button
                className={cn(
                    "relative flex items-center justify-center",
                    "transition-all duration-200 ease-in-out",
                    "focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2",
                    variant === "floating"
                        ? "w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white shadow-lg hover:shadow-xl"
                        : "w-9 h-9 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700",
                    isOpen && "ring-2 ring-blue-400 ring-offset-2"
                )}
                onClick={() => setIsOpen(!isOpen)}
                id={variant === "floating" ? "notification-bell" : "notification-bell-inline"}
                aria-label="Notifications"
            >
                <Bell size={variant === "floating" ? 22 : 18} />
                {unreadCount > 0 && (
                    <span className={cn(
                        "absolute flex items-center justify-center min-w-[20px] h-[20px] px-1 text-[11px] font-bold text-white bg-red-500 rounded-full shadow-sm border-2 border-white animate-in fade-in zoom-in duration-200",
                        variant === "floating" ? "-top-1 -right-1" : "-top-1.5 -right-1.5"
                    )}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </button>
        </div>
    );
};
